import { camelize } from 'humps';
import { memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useGameProgress } from '@ezugi/bootstrap/src/hooks/useGameProgress';
import { useIntl } from '@ezugi/bootstrap/src/hooks/useIntl';
import {
  CALL_BETS,
  CALL_BETS_DECISION,
  INSURANCE_CALLS,
  LAST_BETS,
  NO_MORE_BETS,
  PLACE_YOUR_BETS,
  CANCEL_ROUND,
  SHUFFLE,
} from '@ezugi/constants';
import { ProgressBar } from '@ezugi/primitives';

import { roundActions } from '../../store/actions';
import { progressBarConfigSelector } from '../../store/selectors';
import { progressBarSelector } from '../../store/selectors/ui';

const intlSpec = {
  keys: {
    disconnected: 'game_status.disconnected',
    gameResult: 'game_status.game_result',
    lastBets: 'game_status.last_bets',
    loading: 'game_status.loading',
    noMoreBets: 'game_status.no_more_bets',
    waitForNextGame: 'game_status.wait_for_next_game',
    waitForNextRound: 'game_status.wait_for_next_round',
    placeYourBets: 'game_status.place_your_bets',
    reconnecting: 'game_status.reconnecting',
    tableClosed: 'game_status.table_closed',
    callbets: 'game_status.call_bets',
    insurancecalls: 'game_status.insurance',
    CANCEL_ROUND: 'game_status.round_cancelled',
    shuffle: 'game_status.place_your_bets',
    [SHUFFLE]: 'game_status.place_your_bets', // ERD-1641 special request from PO
  },
};

export type ConnectedProgressBarProps = {
  label?: string;
  className?: string;
  useLastBets?: boolean;
};

function ConnectedProgressBar({
  label: customLabel,
  className,
  useLastBets = true,
}: ConnectedProgressBarProps) {
  const prgrs = useGameProgress({
    startStates: [ PLACE_YOUR_BETS, CALL_BETS, INSURANCE_CALLS ],
    stopStates: [ NO_MORE_BETS, CALL_BETS_DECISION, CANCEL_ROUND ],
    stateColors: {
      [CALL_BETS]: '#b4de85',
      [CALL_BETS_DECISION]: 'transparent',
      [SHUFFLE]:
        'linear-gradient(180deg, #64D384 50%, #44AC5B 78.12%, #3A9A4F 92.19%, #296536 100%)',
    },
  });

  const progressBar = useSelector(progressBarSelector);

  const { hideTimer } = useSelector(progressBarConfigSelector);
  const dispatch = useDispatch();

  const locales = useIntl(intlSpec);

  const { areBetsOpen, timeLeft, state } = prgrs;

  useEffect(() => {
    const updateToLastBets = useLastBets && areBetsOpen && state !== LAST_BETS && timeLeft <= 5 && timeLeft > 0;

    if (updateToLastBets) {
      dispatch(roundActions.round.set({ roundStatus: LAST_BETS }));
    }
  }, [ useLastBets, state, timeLeft, areBetsOpen, dispatch ]);

  return (
    <ProgressBar
      {...{
        ...prgrs,
        ...(hideTimer || prgrs.totalTime <= 0 ? { progress: -1 } : {}),
        ...progressBar,
        label:
          customLabel
          || progressBar.label
          || locales[
            camelize((prgrs.state || '').toLowerCase()) as keyof typeof locales
          ],
        className,
      }}
    />
  );
}

export default memo(ConnectedProgressBar);
